import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import { getDomainFromUrl, getDomainCyberColor, getFaviconCandidates } from '../lib/faviconResolver';
import { parseGitHubRepo, getCachedGitHubTelemetrySync, fetchGitHubTelemetry } from '../lib/githubTelemetry';
import { detectCryptoAsset, getCachedCryptoSync, fetchCryptoTelemetry } from '../lib/cryptoTracker';
import { estimateReadingTime } from '../lib/readingTime';
import { classifyDomainCategory } from '../lib/domainStyler';
import { isRssFeedUrl } from '../lib/rssParser';
import { getAmbientGlow } from '../lib/ambientStyler';
import { calculateHoloTilt, getHoloGlareStyle } from '../lib/holoCardPhysics';

const { FiFolder, FiLink, FiFileText, FiShare2, FiEdit2, FiEye, FiCheck, FiSmartphone, FiRss } = FiIcons;

const PERMISSION_COLORS = {
  editor: { color: '#34d399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.3)' },
  viewer: { color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.3)' },
};

const GridItem = ({
  item, onClick, onContextMenu, isDragging, themeColors, onInfoClick,
  onMoveTo, isSelected = false, hasSelection = false, onToggleSelect, onRssClick, density = 'standard',
}) => {
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [allImgFailed, setAllImgFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [holo, setHolo] = useState(null);
  const [ghData, setGhData] = useState(() => getCachedGitHubTelemetrySync(item.url));
  const [cryptoData, setCryptoData] = useState(() => getCachedCryptoSync(item.url, item.title));

  const touchTimerRef = useRef(null);
  const touchStartPosRef = useRef(null);
  const isLongPressRef = useRef(false);

  const handleTouchStart = (e) => {
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
      isLongPressRef.current = false;

      if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(() => {
        isLongPressRef.current = true;
        if (navigator.vibrate) {
          try { navigator.vibrate(50); } catch (_) {}
        }
        // Select this 1 item!
        if (onToggleSelect) {
          onToggleSelect(item);
        }
        // Also trigger the card's context menu at touch coordinates
        if (onContextMenu) {
          const syntheticEvent = {
            preventDefault: () => {},
            stopPropagation: () => {},
            clientX: touch.clientX,
            clientY: touch.clientY,
          };
          onContextMenu(syntheticEvent, item);
        }
      }, 350);
    }
  };

  const handleTouchMove = (e) => {
    if (touchStartPosRef.current && e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const dist = Math.hypot(
        touch.clientX - touchStartPosRef.current.x,
        touch.clientY - touchStartPosRef.current.y
      );
      if (dist > 8) {
        if (touchTimerRef.current) {
          clearTimeout(touchTimerRef.current);
          touchTimerRef.current = null;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const handleClick = (e) => {
    if (isLongPressRef.current) {
      isLongPressRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (hasSelection && onToggleSelect) {
      onToggleSelect(item);
      return;
    }
    if (onClick) onClick(e);
  };

  const tc = themeColors || {};
  const isFolder = item.type === 'folder';
  const hasNote = item.note?.trim();
  const hasCreds = item.credentials?.trim();
  const domain = !isFolder && item.url ? getDomainFromUrl(item.url) : '';
  const isGitHub = !isFolder && parseGitHubRepo(item.url);
  const isCrypto = !isFolder && detectCryptoAsset(item.url, item.title);
  const readingEstimate = !isFolder ? estimateReadingTime(item) : null;
  const categoryStyle = !isFolder && item.url ? classifyDomainCategory(item.url) : null;
  const isRss = !isFolder && item.url ? isRssFeedUrl(item.url) : false;

  // Clear holo tilt immediately when being dragged
  useEffect(() => {
    if (isDragging) {
      setHolo(null);
    }
  }, [isDragging]);

  // Favicon candidates list
  const candidates = !isFolder && item.url ? getFaviconCandidates(item.url, item.logoUrl) : [];

  useEffect(() => {
    setCandidateIdx(0);
    setAllImgFailed(false);
  }, [item.logoUrl, item.url]);

  // Asynchronously fetch GitHub telemetry
  useEffect(() => {
    if (isGitHub && !ghData) {
      fetchGitHubTelemetry(item.url).then(data => {
        if (data) setGhData(data);
      });
    }
  }, [isGitHub, item.url, ghData]);

  // Asynchronously fetch Crypto telemetry
  useEffect(() => {
    if (isCrypto && !cryptoData) {
      fetchCryptoTelemetry(item.url, item.title).then(data => {
        if (data) setCryptoData(data);
      });
    }
  }, [isCrypto, item.url, item.title, cryptoData]);

  // Shared folder states
  const isShared = item._isShared;
  const isOwnerShared = item._isOwnerShared;
  const permission = item._sharedPermission || 'viewer';
  const permCfg = PERMISSION_COLORS[permission] || PERMISSION_COLORS.viewer;

  // Folder color: purple for shared-with-me, custom folder color, or accent
  const folderColor = isShared ? permCfg.color : (item.color || tc.accent);

  const handleImgError = () => {
    if (candidateIdx + 1 < candidates.length) {
      setCandidateIdx(candidateIdx + 1);
    } else {
      setAllImgFailed(true);
    }
  };

  const domainCyberStyle = domain ? getDomainCyberColor(domain) : null;

  // Density dimensions
  const isCompact = density === 'compact';
  const isCinematic = density === 'cinematic';
  const iconBoxSize = isCompact ? 'w-9 h-9' : isCinematic ? 'w-14 h-14' : 'w-12 h-12';
  const iconSize = isCompact ? 'w-4 h-4' : isCinematic ? 'w-7 h-7' : 'w-5 h-5';
  const titleSize = isCompact ? 'text-[10px]' : isCinematic ? 'text-xs font-semibold' : 'text-[11px] font-medium';

  const ambientGlow = !isFolder && item.url ? getAmbientGlow(item.url, hovered) : null;

  const containerStyle = {
    opacity: isDragging ? 0.35 : 1,
    backgroundColor: isSelected
      ? `${tc.accent}18`
      : hovered
        ? tc.surface || 'rgba(255,255,255,0.06)'
        : 'transparent',
    border: isSelected
      ? `1.5px solid ${tc.accent}`
      : `1.5px solid ${hovered ? (isFolder && item.color ? `${item.color}66` : (ambientGlow?.borderColor || (categoryStyle ? categoryStyle.border : (tc.border || 'rgba(255,255,255,0.12)')))) : 'transparent'}`,
    borderRadius: isCompact ? 12 : isCinematic ? 20 : 16,
    padding: isCompact ? '6px 4px 5px' : isCinematic ? '14px 8px 10px' : '10px 6px 8px',
    cursor: isDragging ? 'grabbing' : 'pointer',
    transition: holo ? 'none' : 'all 0.16s ease',
    position: 'relative',
    transform: holo ? holo.transform : undefined,
    transformStyle: 'preserve-3d',
    boxShadow: isFolder && item.color && hovered
      ? `0 0 16px ${item.color}25`
      : (!isFolder && ambientGlow && hovered
        ? ambientGlow.boxShadow
        : (!isFolder && categoryStyle && hovered
          ? `0 0 18px ${categoryStyle.glow}`
          : 'none')),
  };

  return (
    <motion.div
      style={containerStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseMove={(e) => {
        if (isDragging) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const tilt = calculateHoloTilt(e.clientX, e.clientY, rect, 8);
        setHolo(tilt);
      }}
      onMouseLeave={() => {
        setHovered(false);
        setHolo(null);
      }}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >

      {/* Specular Holographic Glare Sheen */}
      {holo && (
        <div style={getHoloGlareStyle(holo.glareX, holo.glareY, holo.glareOpacity, ambientGlow?.glowColor || tc.accent)} />
      )}
      {/* Folder Top Cyber Gradient Banner */}
      {isFolder && (
        <div
          className="absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl opacity-90 transition-opacity"
          style={{
            background: item.gradient || `linear-gradient(90deg, ${folderColor} 0%, transparent 100%)`,
          }}
        />
      )}

      <div className="flex justify-center mb-1.5">
        <div className={`relative ${iconBoxSize} rounded-2xl flex items-center justify-center overflow-hidden shadow-md`}
          style={{
            backgroundColor: isFolder
              ? (isShared ? permCfg.bg : (item.color ? `${item.color}22` : `${tc.accent}22`))
              : (!allImgFailed && candidates[candidateIdx] ? 'rgba(255,255,255,0.08)' : `${tc.accent}18`),
            border: `1px solid ${isFolder
              ? (isShared ? permCfg.border : (item.color ? `${item.color}55` : `${tc.accent}44`))
              : (tc.border || 'rgba(255,255,255,0.08)')}`,
          }}>
          {isFolder ? (
            <SafeIcon icon={FiFolder} className={iconSize} style={{ color: folderColor }} />
          ) : !allImgFailed && candidates.length > 0 ? (
            <img
              src={candidates[candidateIdx]}
              alt={item.title}
              className={`${isCompact ? 'w-5 h-5' : isCinematic ? 'w-10 h-10' : 'w-8 h-8'} object-contain rounded`}
              onError={handleImgError}
            />
          ) : domainCyberStyle ? (
            // Cyber Monogram Fallback
            <div className={`w-full h-full bg-gradient-to-br ${domainCyberStyle.bg} flex items-center justify-center border ${domainCyberStyle.border}`}>
              <span className={`text-xs font-mono font-bold uppercase ${domainCyberStyle.text}`}>
                {domain.slice(0, 2)}
              </span>
            </div>
          ) : (
            <SafeIcon icon={FiLink} className={iconSize} style={{ color: tc.accent }} />
          )}

          {/* Live Protocol Status Dot on top-left of the icon container */}
          {!isFolder && item.url && (
            <div
              className="absolute top-1 left-1 w-2 h-2 rounded-full"
              style={{
                backgroundColor: item.url.startsWith('https://') ? '#10b981' : '#f59e0b',
                boxShadow: item.url.startsWith('https://') ? '0 0 6px #10b981' : '0 0 6px #f59e0b',
              }}
              title={item.url.startsWith('https://') ? 'Protocol: HTTPS Verified (Secure)' : 'Protocol: Insecure HTTP'}
            />
          )}

          {/* Note / Creds indicators */}
          {(hasNote || hasCreds) && (
            <div className="absolute top-0.5 right-0.5 flex gap-0.5">
              {hasNote && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tc.accent }} />}
              {hasCreds && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#f59e0b' }} />}
            </div>
          )}

          {/* Share indicator icon */}
          {isFolder && (isShared || isOwnerShared) && (
            <div className="absolute bottom-0.5 right-0.5">
              <SafeIcon icon={FiShare2} className="w-2.5 h-2.5"
                style={{ color: isShared ? permCfg.color : tc.accent }} />
            </div>
          )}
        </div>
      </div>

      <p className={`text-center ${titleSize} leading-tight line-clamp-2 px-1 transition-opacity duration-200`}
        style={{ color: tc.text, opacity: hovered ? 1 : 0.85 }}>
        {item.title}
      </p>

      {/* Live Crypto Price Telemetry Pill */}
      {isCrypto && cryptoData && (
        <div className="flex justify-center mt-1">
          <span
            className={`inline-flex items-center gap-1 text-[8px] font-mono px-1.5 py-0.2 rounded-full font-semibold border ${
              cryptoData.isPositive
                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border-rose-500/30'
            }`}
            title={`Live Coin Price: ${cryptoData.symbol} ${cryptoData.price} (${cryptoData.change} 24h)`}
          >
            <span>{cryptoData.symbol}</span>
            <span>{cryptoData.price}</span>
            <span className="opacity-80">{cryptoData.change}</span>
          </span>
        </div>
      )}

      {/* GitHub Repository Live Telemetry Pill */}
      {isGitHub && ghData && ghData.stars && !isCrypto && (
        <div className="flex justify-center mt-1">
          <span
            className="inline-flex items-center gap-1 text-[8px] font-mono px-1.5 py-0.2 rounded-full font-semibold border bg-neutral-900/90 text-purple-300 border-purple-500/30"
            title={`GitHub Repo: ${ghData.fullName} • ${ghData.language || 'Code'}`}
          >
            <span>★</span>
            <span>{ghData.stars}</span>
            {ghData.language && <span className="opacity-70">• {ghData.language}</span>}
          </span>
        </div>
      )}

      {/* Click Frequency Analytics Badge */}
      {item.clicks > 0 && !isGitHub && !isCrypto && (
        <div className="flex justify-center mt-1">
          <span className="inline-flex items-center gap-0.5 text-[8px] font-mono px-1.5 py-0.2 rounded-full font-semibold border"
            style={{
              backgroundColor: 'rgba(16,185,129,0.12)',
              color: '#34d399',
              borderColor: 'rgba(16,185,129,0.25)',
            }}
            title={`${item.clicks} total launches • Last: ${item.lastVisited ? new Date(item.lastVisited).toLocaleDateString() : 'recent'}`}
          >
            ⚡ {item.clicks}
          </span>
        </div>
      )}

      {/* Live Syndicated RSS Feed Badge */}
      {isRss && (
        <div className="flex justify-center mt-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRssClick?.(item);
            }}
            className="inline-flex items-center gap-1 text-[8px] font-mono px-1.5 py-0.2 rounded-full font-bold bg-orange-500/15 text-orange-300 border border-orange-500/35 hover:bg-orange-500/25 transition-all shadow-sm"
            title="Open Live Syndicated RSS Feed Stream"
          >
            <span>📡 RSS</span>
          </button>
        </div>
      )}

      {/* Reading Duration Badge */}
      {readingEstimate && (item.tags?.includes('reading') || hovered) && !isGitHub && !isCrypto && item.clicks === 0 && (
        <div className="flex justify-center mt-0.5">
          <span className="inline-flex items-center text-[7.5px] font-mono px-1.5 py-0.2 rounded-full bg-neutral-800 text-neutral-300 border border-neutral-700/50">
            ⏱️ {readingEstimate.label}
          </span>
        </div>
      )}

      {/* Ephemeral TTL Expiration Badge */}
      {item.expiresAt && (
        <div className="flex justify-center mt-0.5">
          {Date.now() > item.expiresAt ? (
            <span className="inline-flex items-center text-[8px] font-mono px-1.5 py-0.2 rounded-full font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40">
              🔥 Expired
            </span>
          ) : (
            <span className="inline-flex items-center text-[8px] font-mono px-1.5 py-0.2 rounded-full font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30">
              ⏳ {Math.max(1, Math.round((item.expiresAt - Date.now()) / (1000 * 60 * 60)))}h left
            </span>
          )}
        </div>
      )}

      {/* Permission badge */}
      {isFolder && isShared && (
        <div className="flex justify-center mt-1">
          <span className="inline-flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: permCfg.bg,
              color: permCfg.color,
              border: `1px solid ${permCfg.border}`,
            }}>
            <SafeIcon icon={permission === 'editor' ? FiEdit2 : FiEye} className="w-2 h-2" />
            {permission === 'editor' ? 'Editor' : 'Viewer'}
          </span>
        </div>
      )}

      {/* Batch selection button */}
      {onToggleSelect && (hovered || isSelected || hasSelection) && (
        <motion.button
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(item); }}
          className="absolute top-1.5 left-1.5 w-5 h-5 rounded-md flex items-center justify-center transition-all shadow-sm z-20 cursor-pointer"
          style={{
            backgroundColor: isSelected ? tc.accent : 'rgba(0,0,0,0.65)',
            border: `1px solid ${isSelected ? tc.accent : 'rgba(255,255,255,0.3)'}`,
            color: isSelected ? '#000' : '#fff',
            opacity: isSelected ? 1 : hovered ? 0.9 : 0.6,
          }}
          title={isSelected ? 'Deselect item' : 'Select for batch action'}
        >
          {isSelected ? (
            <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" />
          ) : (
            <div className="w-1.5 h-1.5 rounded-sm bg-white/50" />
          )}
        </motion.button>
      )}

      {/* 1-Click Move to Folder action button for BOTH bookmarks and folders */}
      {hovered && onMoveTo && (!isShared || permission === 'editor') && (
        <motion.button
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveTo(item); }}
          className={`absolute top-1.5 ${!isFolder && onInfoClick ? 'right-7.5' : 'right-1.5'} w-5 h-5 rounded-full flex items-center justify-center transition-all shadow-sm z-20`}
          style={{
            backgroundColor: 'rgba(0,0,0,0.75)',
            border: `1px solid ${tc.accent ? `${tc.accent}66` : 'rgba(255,255,255,0.3)'}`,
            color: tc.accent || '#10b981',
          }}
          title="Move to Folder..."
        >
          <SafeIcon icon={FiFolder} className="w-2.5 h-2.5" />
        </motion.button>
      )}

      {/* Info button — links only */}
      {!isFolder && hovered && onInfoClick && (!isShared || permission === 'editor') && (
        <motion.button
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          type="button"
          onClick={(e) => { e.stopPropagation(); onInfoClick(item); }}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center z-20"
          style={{ backgroundColor: `${tc.accent}33`, border: `1px solid ${tc.accent}55` }}
          title="Notes & Credentials"
        >
          <SafeIcon icon={FiFileText} className="w-2.5 h-2.5" style={{ color: tc.accent }} />
        </motion.button>
      )}
    </motion.div>
  );
};

export default GridItem;